import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { objectNotNullV2 } from '@/Utils/FnUtils';
import { handleResponseThrowError } from '@/_utils/services';
import type { ENUM_CLOUD_ACTIVATION_REQUEST_STATUS } from '../constants';
import { GET_PLACES } from '../constants';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import type { BaseRequestResponse } from '@/_types/services';
import type { Place } from '@/_types/places';
import { apiClient } from '@/_config/lib/axios/api-client';

export type GetPlacesProps = {
  placeName?: string;
  cityId?: string;
  ibgeCode?: string;
  limit?: number;
  page?: number;
  establishmentId?: string;
  deliveryType?: string;
  hasFare?: boolean;
  statusCloudActivationRequest?: ENUM_CLOUD_ACTIVATION_REQUEST_STATUS[];
};

const getPlaces = (params: GetPlacesProps) => {
  const {
    placeName,
    cityId,
    ibgeCode,
    limit,
    page,
    deliveryType,
    establishmentId,
    hasFare,
    statusCloudActivationRequest,
  } = params || {};

  const newPage = page || 1;
  const newLimit = limit || DEFAULT_LIMIT_REQUEST;
  const offset = (newPage - 1) * newLimit;

  return apiClient
    .get<BaseRequestResponse<Place[]>>('/estabelecimentos/listagem', {
      params: objectNotNullV2({
        estabelecimentoId: establishmentId,
        tipoEntrega: deliveryType,
        nomeExibicao: placeName,
        municipioId: cityId === ' ' ? undefined : cityId,
        codigoIbge: ibgeCode,
        hasFare,
        statusCloudActivationRequest,
        limit: newLimit,
        offset,
      }),
    })
    .then(response => response.data as unknown as BaseRequestResponse<Place[]>)
    .catch(handleResponseThrowError());
};

type GetPlacesData = Awaited<ReturnType<typeof getPlaces>>;

export const useGetPlaces = (
  params: GetPlacesProps,
  options: Omit<
    UseQueryOptions<
      GetPlacesData,
      unknown,
      GetPlacesData,
      (string | GetPlacesProps)[]
    >,
    'queryKey' | 'queryFn'
  > = {},
) => {
  return useQuery({
    queryKey: [GET_PLACES, params],
    queryFn: () => getPlaces(params),
    ...options,
  });
};
